import { useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useChatStore } from "@stores/chatStore";
import { FanPageMenu } from "@layouts/FanPageMenu";
import { FanPageBody } from "@layouts/FanPageBody";
import { useAppDispatch, useAppSelector } from "@stores/redux/store";
import { STORAGE_KEYS } from "@config/app.config";
import { getLocalStorage, CHANNELS, PLATFORM_IDS } from "@common";
import { useNotificationContext } from "@hooks/useNotificationContext";
import classNames from "classnames";
import { useConversationMessages } from "./hooks/useConversationMessages";
import { useConversationSend } from "./hooks/useConversationSend";
import { useFanpageBootstrap } from "./hooks/useFanpageBootstrap";
import { useFanpageSelectionEffects } from "./hooks/useFanpageSelectionEffects";
import { useFanpageMenuState } from "./hooks/useFanpageMenuState";
import { useFirstFacebookPageSelect } from "./hooks/useFirstFacebookPageSelect";
import styles from "./styles.module.less";

export const FanPageLayout = ({
  isShowInfoUser,
}: {
  isShowInfoUser?: boolean;
}) => {
  const {
    conversationId: pathConversationId,
    platform: pathPlatform,
    pageId: pathPageId,
  } = useParams<{
    conversationId?: string;
    platform?: string;
    pageId?: string;
  }>();
  const urlConversationId = pathConversationId;
  const urlPlatform = pathPlatform || undefined;
  const urlPageId = pathPageId || undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const { error: showError } = useNotificationContext();
  const dispatch = useAppDispatch();
  const {
    isSidebarOpen,
    isFanpageMenuOpen,
    handleToggleFanpageMenu,
    handleToggleUserSidebar,
    openFanpageMenu,
  } = useFanpageMenuState();
  const selectedChannel = useChatStore((state) => state.selectedChannel);
  const isSwitchingFanpage = useChatStore((state) => state.isSwitchingFanpage);
  const selectedFanpageName = useChatStore(
    (state) => state.selectedFanpageName
  );
  const selectedConversationId = useChatStore(
    (state) => state.selectedConversationId
  );
  const selectedPageId = useChatStore((state) => state.selectedPageId);
  const selectFanpage = useChatStore((state) => state.selectFanpage);
  const selectConversation = useChatStore((state) => state.selectConversation);
  const markConversationAsRead = useChatStore(
    (state) => state.markConversationAsRead
  );
  const setIsSyncing = useChatStore((state) => state.setIsSyncing);
  const conversations = useChatStore((state) => state.conversations);
  const filteredConversations = useChatStore(
    (state) => state.filteredConversations
  );

  const isValidForSelection = useCallback(
    (conv?: { id: string; channel: string; fanpageName?: string }) => {
      if (!conv) return false;
      if (conv.channel !== selectedChannel) return false;
      if (
        selectedFanpageName &&
        conv.fanpageName &&
        conv.fanpageName !== selectedFanpageName
      ) {
        return false;
      }
      return true;
    },
    [selectedChannel, selectedFanpageName]
  );

  const activeConversation = useMemo(() => {
    const conversationIdToUse = urlConversationId || selectedConversationId;
    if (!conversationIdToUse) {
      return undefined;
    }

    // Only find conversation in filteredConversations to ensure it's visible in current tab
    // This ensures that when conversation state changes and it's removed from filtered list,
    // activeConversation will become undefined and chat window will be hidden
    const conversation = filteredConversations.find(
      (conversation) => conversation.id === conversationIdToUse
    );

    if (!conversation) {
      return undefined;
    }

    if (!isValidForSelection(conversation)) {
      return undefined;
    }

    return conversation;
  }, [
    filteredConversations,
    selectedConversationId,
    urlConversationId,
    isValidForSelection,
  ]);

  const messagesStore = useAppSelector((state) => state.messages);

  const { pages: facebookPages, loading: fanpagesLoading } = useAppSelector(
    (state) => state.fanpage
  );

  const { oas: zaloOAs } = useAppSelector((state) => state.zalo);

  const platformFromChannel = useCallback((channel: string) => {
    if (channel === CHANNELS.FACEBOOK) return PLATFORM_IDS.FACEBOOK;
    return channel;
  }, []);

  const buildFanpageUrl = useCallback(() => "/conversations", []);

  const resolvedPageId = useMemo(() => {
    if (selectedPageId) return selectedPageId;
    if (!selectedFanpageName) return undefined;
    if (selectedChannel === CHANNELS.FACEBOOK) {
      return facebookPages.find((page) => page.name === selectedFanpageName)
        ?.id;
    }
    if (selectedChannel === CHANNELS.ZALO) {
      return zaloOAs.find((oa) => oa.name === selectedFanpageName)?.id;
    }
    return undefined;
  }, [
    selectedPageId,
    selectedFanpageName,
    selectedChannel,
    facebookPages,
    zaloOAs,
  ]);

  const currentPage = useMemo(() => {
    if (!selectedFanpageName) return null;
    return (
      facebookPages.find((page) => page.name === selectedFanpageName) || null
    );
  }, [selectedFanpageName, facebookPages]);

  const { hasClearedNotSyncedRef } = useFanpageSelectionEffects({
    filteredConversations,
    selectedFanpageName,
    selectedConversationId,
    selectedChannel,
    conversations,
    selectConversation,
    facebookPages,
    messagesByConversation: messagesStore.messagesByConversation,
    setIsSyncing,
    navigate,
    locationPathname: location.pathname,
    urlConversationId,
    urlPlatform,
    urlPageId,
    isSwitchingFanpage,
    platformFromChannel,
    resolvedPageId,
    buildFanpageUrl,
  });

  const {
    currentConversationId,
    currentMessages,
    isLoading,
    hasMore,
    handleLoadMore,
  } = useConversationMessages({
    activeConversation,
    selectedConversationId,
    selectedFanpageName,
    selectedChannel,
    facebookPages,
    zaloOAs,
    messagesStore,
    dispatch,
    markConversationAsRead,
    urlConversationId,
    hasClearedNotSyncedRef,
  });

  useFanpageBootstrap(dispatch);
  useFirstFacebookPageSelect({
    facebookPages,
    fanpagesLoading,
    selectedFanpageName,
    selectedPageId,
    selectedChannel,
    selectFanpage,
    locationPathname: location.pathname,
  });
  const token = getLocalStorage(STORAGE_KEYS.AUTH_TOKEN) || undefined;

  const { handleSend } = useConversationSend({
    token,
    activeConversation,
    currentConversationId,
    selectedFanpageName,
    facebookPages,
    messagesStore,
    dispatch,
    showError,
  });

  const handleDownload = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <div
      className={classNames(styles.chatLayout, {
        [styles.sidebarCollapsed]: !isFanpageMenuOpen,
      })}
    >
      <FanPageMenu
        isFanpageMenuOpen={isFanpageMenuOpen}
        handleToggleFanpageMenu={handleToggleFanpageMenu}
      />
      <FanPageBody
        activeConversation={activeConversation}
        messages={currentMessages}
        isFetching={isLoading}
        loadMore={handleLoadMore}
        hasMore={hasMore}
        onSend={handleSend}
        onDownload={handleDownload}
        isSidebarOpen={isSidebarOpen}
        onToggleUserSidebar={handleToggleUserSidebar}
        onOpenFanpageMenu={openFanpageMenu}
        pageId={
          activeConversation?.channel === CHANNELS.FACEBOOK &&
          selectedFanpageName
            ? facebookPages.find((page) => page.name === selectedFanpageName)
                ?.id || null
            : null
        }
        selectedFanpageName={selectedFanpageName}
        filteredConversations={filteredConversations}
        messageSyncStatus={currentPage?.messageSyncStatus}
        isShowInfoUser={isShowInfoUser}
      />
    </div>
  );
};
